import React, { useState } from "react";
import { isFreshlyAdded } from "../constants/newShelf";
import { GOLD } from "../constants/theme";
import { Ic } from "../constants/icons";
import { Pop } from "../shared/motion";
import { useDoubleTap } from "../../utils/useGestures";
import { haptic as bamHaptic } from "../../utils/nativeBridge";
import { useCoaches } from "../hooks/useCoaches";
import { logEvent } from "../utils/analytics";
import { coverSources } from "../masterclass/coverArt";

// The masterclass card. Art, one title, one line, one meta row. The old
// shared ContentCard said the category three times (eyebrow, chip, filter)
// and the title twice (poster, caption), and stacked FREE / NEW / GUEST
// badges over the eyebrow text. Everything that carried no information at
// a glance is gone; what is left is what a coach scans a shelf for.
//
// The poster is text-free so the title sits on it once, in the same Bebas
// every other display header uses. It prefers a real frame from the class's
// own video over the generated mc-art-*.svg set, which is one drawing in
// eleven colours and reads as a single texture at shelf size. See
// masterclass/coverArt.js for the order and why.

const fmtDrop = (ts) =>
  new Date(ts).toLocaleDateString(undefined, { month: "short", day: "numeric" });

export const MasterclassCard = React.memo(function MasterclassCard({
  item, C, onClick, favDrills = [], toggleFav, masterProgress, dim = false,
  sectionId = "master", mod = null,
}) {
  const coaches = useCoaches();
  const coach = item.coachId ? coaches?.byId?.[item.coachId] : null;
  const isFav = favDrills.some((f) => f.key === `${sectionId}-${item.id}`);
  // Walk the candidates rather than taking the best one: a Mux id can 404
  // and the card should fall through to the next option, not to an empty box.
  const art = coverSources(item, mod, { width: 640, height: 360 });
  const [artIdx, setArtIdx] = useState(0);
  const src = art[artIdx] || null;
  const isPhoto = src != null && !src.endsWith(".svg");
  const [flash, setFlash] = useState(false);

  const dt = useDoubleTap({
    onSingleTap: () => {
      try { logEvent("masterclass_opened", { kind: sectionId, content_id: item.id, title: item.title }); } catch {}
      onClick(item);
    },
    onDoubleTap: () => {
      if (toggleFav) toggleFav(item, sectionId);
      setFlash(true);
      setTimeout(() => setFlash(false), 700);
    },
    enabled: !!toggleFav,
  });

  const dropTs = item.availableAt ? new Date(item.availableAt).getTime() : 0;
  const hasDropDate = dropTs && dropTs > Date.now();
  const preRelease = item.comingSoon || hasDropDate;
  // Same rule as the New shelf, from the same place, so the badge and the
  // shelf cannot disagree about what counts as new. Previously this was its
  // own copy of the window, and it also required the hand-set `isNew` flag,
  // which meant a release could sit on the shelf with no badge.
  const isNew = isFreshlyAdded(item);
  const prog = masterProgress ? masterProgress[`master-${item.id}`] : null;

  // One status word, top-left, only when there is one. "Free" and "New"
  // are the two a coach acts on; a drop date is the third.
  const status = preRelease
    ? (hasDropDate ? `Drops ${fmtDrop(dropTs)}` : "Coming soon")
    : item.free ? "Free" : isNew ? "New" : null;

  const meta = [
    item.dateLabel,
    item.duration,
    item.guest && item.guestName ? `with ${item.guestName}` : coach ? `with ${coach.name}` : null,
    item.qaCount ? `${item.qaCount} question${item.qaCount === 1 ? "" : "s"}` : null,
  ].filter(Boolean).join(" · ");

  return (
    <div className="bam-mc-card" onClick={dt.onClick}
      style={{
        position: "relative", borderRadius: 14, overflow: "hidden",
        background: C.cardGradient, border: `1px solid ${C.cardBorder}`,
        boxShadow: `${C.surfaceShadow}, ${C.innerGlow}`, cursor: "pointer",
        ...(dim && preRelease ? { opacity: 0.6, filter: "saturate(0.6)" } : null),
      }}>
      {flash && (
        <div style={{ position: "absolute", inset: 0, zIndex: 10, pointerEvents: "none",
          display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div style={{ background: `radial-gradient(circle, ${GOLD}30 0%, ${GOLD}00 65%)`,
            width: "70%", height: "70%", display: "flex", alignItems: "center",
            justifyContent: "center", animation: "bamHeartBurst .65s ease-out forwards" }}>
            <svg width="64" height="64" viewBox="0 0 24 24" fill={GOLD}>
              <path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.29 1.51 4.04 3 5.5l7 7Z"/>
            </svg>
          </div>
        </div>
      )}

      {/* Poster: art, then the title on it once. */}
      <div className="bam-mc-poster" style={{ position: "relative", aspectRatio: "16 / 9",
        background: "#0f0f0e", overflow: "hidden" }}>
        {src && (
          <img src={src} alt="" onError={() => setArtIdx((i) => i + 1)}
            loading="lazy" decoding="async"
            style={{ position: "absolute", inset: 0, width: "100%", height: "100%",
              objectFit: "cover", display: "block" }}/>
        )}
        {/* A real frame is bright and busy where the generated art was dark
            and empty, so the title and the status word need their own floor
            to sit on. The SVGs are already dark enough that the heavier
            scrim costs them nothing. */}
        <div style={{ position: "absolute", inset: 0, background: isPhoto
          ? "linear-gradient(180deg, rgba(0,0,0,0.42) 0%, rgba(0,0,0,0.12) 38%, rgba(0,0,0,0.58) 72%, rgba(0,0,0,0.88) 100%)"
          : "linear-gradient(180deg, rgba(0,0,0,0.05) 30%, rgba(0,0,0,0.72) 100%)" }}/>
        {status && (
          <div style={{ position: "absolute", top: 10, left: 12, fontSize: 10, fontWeight: 800,
            letterSpacing: 1.2, textTransform: "uppercase", color: GOLD,
            textShadow: isPhoto ? "0 1px 6px rgba(0,0,0,0.85)" : "none" }}>
            {status}
          </div>
        )}
        {toggleFav && <div onClick={(e) => { e.stopPropagation(); bamHaptic("light"); toggleFav(item, sectionId); }}
          title={isFav ? "Remove bookmark" : "Save this masterclass"}
          className={`btn bam-mc-save${isFav ? " on" : ""}`}
          style={{ position: "absolute", top: 6, right: 6, width: 32, height: 32, borderRadius: "50%",
            display: "flex", alignItems: "center", justifyContent: "center", zIndex: 2,
            background: isFav ? "rgba(226,221,159,0.28)" : "rgba(0,0,0,0.45)" }}>
          <Pop on={isFav}><Ic.Bookmark c={isFav ? GOLD : "#fff"} f={isFav} s={13}/></Pop>
        </div>}
        <div style={{ position: "absolute", left: 14, right: 14, bottom: 12,
          fontFamily: "'Bebas Neue', sans-serif", fontSize: 26, lineHeight: 0.98, letterSpacing: 1,
          color: "#F5F5F5", textShadow: "0 2px 12px rgba(0,0,0,0.6)",
          display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
          {item.title}
        </div>
      </div>

      {/* One line on what it is, then the facts. A Q&A shows its questions
          instead: that list is the pitch, and a coach browsing the shelf is
          looking for the one that is theirs. */}
      <div style={{ padding: "11px 14px 14px", position: "relative",
        // Continues the poster scrim for 18px so there is no hard edge where
        // the darkened photo stops and the lighter card body begins. Most
        // visible in light mode, where the step is 0.88 black against near
        // white, but it shows on dark too once the art is a real photograph.
        backgroundImage: isPhoto
          ? "linear-gradient(180deg, rgba(0,0,0,0.80) 0px, rgba(0,0,0,0) 18px)"
          : "none" }}>
        {Array.isArray(item.qaQuestions) && item.qaQuestions.length > 0 ? (
          <div style={{ display: "grid", gap: 5 }}>
            {item.qaQuestions.slice(0, 4).map((q, i) => (
              <div key={i} style={{ display: "flex", gap: 7, fontSize: 12.5, color: C.textMid, lineHeight: 1.4, minWidth: 0 }}>
                <span style={{ color: GOLD, flexShrink: 0, fontWeight: 800 }}>Q</span>
                <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{q}</span>
              </div>
            ))}
            {item.qaQuestions.length > 4 && (
              <div style={{ fontSize: 11, color: C.textDim, paddingLeft: 18 }}>
                +{item.qaQuestions.length - 4} more
              </div>
            )}
          </div>
        ) : item.sub && (
          <div style={{ fontSize: 12.5, color: C.textMid, lineHeight: 1.45,
            display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
            {item.sub}
          </div>
        )}
        {meta && (
          <div style={{ fontSize: 11, color: C.textDim, marginTop: 7, fontVariantNumeric: "tabular-nums" }}>
            {meta}
          </div>
        )}
      </div>

      {/* Read progress. The track used to be C.border, which is
          rgba(255,255,255,0.06) on dark: invisible. So all you saw was the
          gold portion stopping part way across and it read as a gradient cut
          off rather than as a bar that is partly full. Gold at low alpha
          makes the remaining length visible, which is the whole point of a
          progress bar. Hidden under 3% so a single scroll does not leave a
          stub in the corner. */}
      {prog >= 3 && (
        <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, height: 3,
          background: "rgba(226,221,159,0.17)" }}>
          <div className="bam-fill" style={{ height: "100%", width: `${Math.min(prog, 100)}%`, background: GOLD }}/>
        </div>
      )}
    </div>
  );
});
